import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PageShell } from "@/components/layout/AppLayout";
import { GlassCard } from "@/components/glass/GlassCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Gavel, Info, CheckCircle, ArrowsClockwise, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function DocTypeWorkflows() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/doc-types").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <PageShell title="Document Types → Workflows" subtitle="Admin"><GlassCard className="p-6">Loading…</GlassCard></PageShell>;
  }

  const save = async (doc_type, { workflow_id, version_strategy, pinned_version, preset_id }) => {
    setSaving(doc_type);
    try {
      await api.patch("/doc-types/mapping", { doc_type, workflow_id, version_strategy, pinned_version, preset_id });
      toast.success("Mapping saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };
  const clear = async (doc_type) => {
    try {
      await api.delete(`/doc-types/mapping/${doc_type}`);
      toast.success("Mapping cleared");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };

  return (
    <PageShell title="Document Types → Workflows" subtitle="Admin · Approval routing">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Alert className="mb-6" data-testid="dtw-explainer">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Assign an approval workflow to each document type. When someone clicks <strong>Submit for approval</strong> on a contract,
            Clariva picks the mapped workflow and starts an instance automatically. Choose <em>Latest published</em> to track the head version,
            or pin to a specific version for compliance-locked flows.
          </AlertDescription>
        </Alert>

        <GlassCard className="p-0 overflow-hidden" data-testid="dtw-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface-2)]/60 border-b border-[var(--border)]">
                <tr>
                  <th className="text-left font-medium px-4 py-3 min-w-[220px]">Document type</th>
                  <th className="text-left font-medium px-4 py-3">Workflow</th>
                  <th className="text-left font-medium px-4 py-3 w-[220px]">Version</th>
                  <th className="text-left font-medium px-4 py-3 w-[200px]">Style preset</th>
                  <th className="text-left font-medium px-4 py-3 w-[90px]">Status</th>
                  <th className="text-right font-medium px-4 py-3 w-[90px]"></th>
                </tr>
              </thead>
              <tbody>
                {data.doc_types.map((dt) => (
                  <DocTypeRow
                    key={dt.id}
                    docType={dt}
                    mapping={data.mappings[dt.id]}
                    workflows={data.workflows}
                    presets={data.presets || []}
                    onSave={(v) => save(dt.id, v)}
                    onClear={() => clear(dt.id)}
                    saving={saving === dt.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </motion.div>
    </PageShell>
  );
}

function DocTypeRow({ docType, mapping, workflows, presets, onSave, onClear, saving }) {
  const [workflow_id, setWorkflowId] = useState(mapping?.workflow_id || "");
  const [strategy, setStrategy] = useState(mapping?.version_strategy || "latest");
  const [pinned, setPinned] = useState(mapping?.pinned_version || null);
  const [preset_id, setPresetId] = useState(mapping?.preset_id || "");

  useEffect(() => {
    setWorkflowId(mapping?.workflow_id || "");
    setStrategy(mapping?.version_strategy || "latest");
    setPinned(mapping?.pinned_version || null);
    setPresetId(mapping?.preset_id || "");
  }, [mapping]);

  const selectedGroup = workflows.find((g) => g.versions.some((v) => v.id === workflow_id));
  const dirty = (workflow_id || "") !== (mapping?.workflow_id || "") ||
                strategy !== (mapping?.version_strategy || "latest") ||
                (strategy === "pinned" && pinned !== mapping?.pinned_version) ||
                (preset_id || "") !== (mapping?.preset_id || "");

  return (
    <tr className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]/30" data-testid={`dtw-row-${docType.id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{docType.label}</div>
        <div className="text-[10px] text-[var(--text-3)]">
          <code className="px-1.5 py-0.5 rounded bg-[var(--surface-2)]">{docType.id}</code>
          {docType.source === "discovered" && <Badge variant="outline" className="ml-1 text-[9px] h-4">from data</Badge>}
        </div>
      </td>
      <td className="px-4 py-3">
        <Select
          value={workflow_id || "__none__"}
          onValueChange={(v) => { setWorkflowId(v === "__none__" ? "" : v); if (v === "__none__") setStrategy("latest"); }}
        >
          <SelectTrigger className="h-9 w-full max-w-[320px]" data-testid={`dtw-workflow-${docType.id}`}>
            <SelectValue placeholder="Unassigned — manual approval" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Unassigned — manual approval</SelectItem>
            {workflows.map((g) => (
              <SelectGroupOption key={g.name} group={g} />
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3">
        {workflow_id ? (
          <div className="flex items-center gap-2">
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger className="h-9 w-[140px]" data-testid={`dtw-strategy-${docType.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest published</SelectItem>
                <SelectItem value="pinned">Pin version</SelectItem>
              </SelectContent>
            </Select>
            {strategy === "pinned" && selectedGroup && (
              <Select value={String(pinned || selectedGroup.versions[0].version)} onValueChange={(v) => setPinned(parseInt(v))}>
                <SelectTrigger className="h-9 w-[80px]" data-testid={`dtw-pinned-${docType.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {selectedGroup.versions.map((v) => (
                    <SelectItem key={v.id} value={String(v.version)}>v{v.version}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          <span className="text-xs text-[var(--text-3)] italic">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Select value={preset_id || "__none__"} onValueChange={(v) => setPresetId(v === "__none__" ? "" : v)}>
          <SelectTrigger className="h-9 w-full" data-testid={`dtw-preset-${docType.id}`}>
            <SelectValue placeholder="Tenant default" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Tenant default styling</SelectItem>
            {(presets || []).map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3">
        {mapping ? (
          <Badge className="gap-1 bg-emerald-500/15 text-emerald-600 border-0"><CheckCircle size={10} weight="fill" /> mapped</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">unset</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {dirty ? (
          <Button size="sm" onClick={() => onSave({ workflow_id: workflow_id || null, version_strategy: strategy, pinned_version: strategy === "pinned" ? pinned : null, preset_id: preset_id || null })} disabled={saving} data-testid={`dtw-save-${docType.id}`}>
            {saving ? <ArrowsClockwise size={13} className="animate-spin" /> : "Save"}
          </Button>
        ) : mapping ? (
          <Button size="icon" variant="ghost" onClick={onClear} data-testid={`dtw-clear-${docType.id}`} className="h-7 w-7 text-[var(--text-3)] hover:text-[var(--danger)]">
            <X size={12} />
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function SelectGroupOption({ group }) {
  const latest = group.versions[0];
  return <SelectItem value={latest.id}>{group.name} · v{latest.version}</SelectItem>;
}
